import { useState, useEffect, useCallback } from 'react';
import {
  Globe, FileUp, Search, Plus, RefreshCw, Trash2, Edit, Eye,
  File, FileText, Link2, MoreHorizontal, X, Upload, Database,
  ChevronDown, ExternalLink, Hash, Clock, Filter, Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import {
  getKBDocuments, getKBDocument, updateKBDocument, deleteKBDocument,
  resyncDocument, ingestURL, ingestFile, searchKB, getKBStats
} from '../lib/kb-api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const SOURCE_CONFIG = {
  url: { icon: Globe, label: 'URL', color: 'text-blue-400', bg: 'bg-blue-500/15' },
  file: { icon: FileUp, label: 'File', color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  manual: { icon: FileText, label: 'Manual', color: 'text-violet-400', bg: 'bg-violet-500/15' },
};

const CATEGORIES = ['General', 'Product', 'Support', 'Sales', 'Technical', 'FAQ', 'Guides'];

// ─── URL Ingest Dialog ──────────────────────────────────────

function URLIngestDialog({ open, onOpenChange, onSuccess }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('General');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!url.trim()) { toast.error('URL is required'); return; }
    setLoading(true);
    try {
      const res = await ingestURL({ url: url.trim(), title: title.trim() || null, category });
      toast.success(`Ingested: ${res.data.chunks} chunks from URL`);
      setUrl(''); setTitle(''); setCategory('General');
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to ingest URL');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="url-ingest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe className="w-5 h-5 text-blue-400" />Ingest URL</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Website URL *</Label>
            <Input data-testid="url-input" placeholder="https://example.com/page" value={url} onChange={e => setUrl(e.target.value)} />
          </div>
          <div>
            <Label>Title (auto-detected if empty)</Label>
            <Input data-testid="url-title-input" placeholder="Page title" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="url-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="gold-glow" data-testid="url-ingest-submit">
            {loading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scraping...</> : <><Globe className="w-4 h-4 mr-2" />Ingest URL</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── File Upload Dialog ─────────────────────────────────────

function FileUploadDialog({ open, onOpenChange, onSuccess }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('General');
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (f) => {
    const allowed = ['.pdf', '.docx', '.txt', '.csv'];
    const ext = '.' + f.name.split('.').pop().toLowerCase();
    if (!allowed.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${allowed.join(', ')}`);
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleSubmit = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title.trim() || file.name);
      formData.append('category', category);
      const res = await ingestFile(formData);
      toast.success(`Processed: ${res.data.chunks} chunks from file`);
      setFile(null); setTitle(''); setCategory('General');
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process file');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="file-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileUp className="w-5 h-5 text-emerald-400" />Upload File</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
              dragOver ? "border-primary bg-primary/5" : "border-border/60 hover:border-muted-foreground/40",
              file && "border-emerald-500/50 bg-emerald-500/5"
            )}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}
            onClick={() => document.getElementById('kb-file-input').click()}
            data-testid="file-dropzone"
          >
            <input id="kb-file-input" type="file" className="hidden" accept=".pdf,.docx,.txt,.csv" onChange={e => e.target.files[0] && handleFile(e.target.files[0])} />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <File className="w-5 h-5 text-emerald-400" />
                <span className="text-sm font-medium">{file.name}</span>
                <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(0)} KB)</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={e => { e.stopPropagation(); setFile(null); }}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Drop file here or click to browse</p>
                <p className="text-xs text-muted-foreground/60 mt-1">PDF, DOCX, TXT, CSV (max 10MB)</p>
              </>
            )}
          </div>
          <div>
            <Label>Title</Label>
            <Input data-testid="file-title-input" placeholder="Document title" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="file-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !file} className="gold-glow" data-testid="file-upload-submit">
            {loading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Processing...</> : <><Upload className="w-4 h-4 mr-2" />Upload & Process</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Document Detail Dialog ─────────────────────────────────

function DocumentDetailDialog({ open, onOpenChange, docId }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !docId) return;
    setLoading(true);
    getKBDocument(docId).then(res => setDoc(res.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [open, docId]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh]" data-testid="document-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {doc && (() => { const S = SOURCE_CONFIG[doc.source_type]; return S ? <S.icon className={cn("w-5 h-5", S.color)} /> : null; })()}
            {doc?.title || 'Loading...'}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : doc ? (
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="secondary"><Hash className="w-3 h-3 mr-1" />{doc.total_chunks} chunks</Badge>
                <Badge variant="secondary"><FileText className="w-3 h-3 mr-1" />{doc.char_count?.toLocaleString()} chars</Badge>
                <Badge variant="secondary">{doc.category}</Badge>
                {doc.source_type === 'url' && <Badge variant="secondary" className="text-blue-400"><Link2 className="w-3 h-3 mr-1" />{doc.source_ref}</Badge>}
                {doc.source_type === 'file' && <Badge variant="secondary" className="text-emerald-400"><File className="w-3 h-3 mr-1" />{doc.source_ref}</Badge>}
              </div>
              <div className="space-y-3">
                {doc.chunks?.map((chunk, i) => (
                  <div key={chunk.id} className="p-3 rounded-lg bg-muted/20 border border-border/30">
                    <span className="text-[10px] text-muted-foreground font-mono">Chunk {i + 1}</span>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed whitespace-pre-wrap">{chunk.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ──────────────────────────────────────────────

export const KnowledgeHubPage = () => {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [detailDocId, setDetailDocId] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (sourceFilter !== 'all') params.source_type = sourceFilter;
      const [docsRes, statsRes] = await Promise.all([getKBDocuments(params), getKBStats()]);
      setDocuments(docsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  }, [search, sourceFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (id) => {
    try {
      await deleteKBDocument(id);
      toast.success('Document deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleResync = async (id) => {
    try {
      const res = await resyncDocument(id);
      toast.success(`Re-synced: ${res.data.chunks} chunks updated`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-sync failed');
    }
  };

  const handleEditSave = async () => {
    if (!editingDoc) return;
    try {
      await updateKBDocument(editingDoc.id, { title: editTitle, category: editCategory });
      toast.success('Updated');
      setEditingDoc(null);
      loadData();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const totalSources = (stats?.by_source?.url || 0) + (stats?.by_source?.file || 0) + (stats?.by_source?.manual || 0);

  return (
    <div className="p-6 space-y-6" data-testid="knowledge-hub-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Knowledge Hub</h1>
          <p className="text-muted-foreground mt-1">Manage AI knowledge from URLs, files, and manual entries</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setUrlDialogOpen(true)} data-testid="add-url-btn">
            <Globe className="w-4 h-4 mr-2" />Add URL
          </Button>
          <Button className="gold-glow" onClick={() => setFileDialogOpen(true)} data-testid="add-file-btn">
            <FileUp className="w-4 h-4 mr-2" />Upload File
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Card className="bg-card/50 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-primary">{totalSources}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Sources</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{stats?.by_source?.url || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">URLs</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{stats?.by_source?.file || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Files</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border/60">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-violet-400">{stats?.total_chunks || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Knowledge Chunks</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)} data-testid="kb-search-input" />
          {search && <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearch('')}><X className="w-3.5 h-3.5 text-muted-foreground" /></button>}
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[160px]" data-testid="kb-source-filter">
            <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="url">URLs</SelectItem>
            <SelectItem value="file">Files</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Document List */}
      <Card className="bg-card/50 border-border/60">
        <ScrollArea className="h-[calc(100vh-420px)]">
          <div className="min-w-[600px]">
            <div className="grid grid-cols-[2.5fr_1fr_0.8fr_0.8fr_0.5fr] gap-3 px-4 py-3 border-b border-border/60 text-xs text-muted-foreground uppercase tracking-wider font-medium sticky top-0 bg-card/90 backdrop-blur-sm z-10">
              <span>Document</span>
              <span>Category</span>
              <span>Chunks</span>
              <span>Added</span>
              <span></span>
            </div>

            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : documents.length === 0 ? (
              <div className="p-12 text-center">
                <Database className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No documents found</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Add URLs or upload files to build your knowledge base</p>
              </div>
            ) : (
              documents.map(doc => {
                const srcCfg = SOURCE_CONFIG[doc.source_type] || SOURCE_CONFIG.manual;
                const SrcIcon = srcCfg.icon;
                return (
                  <div key={doc.id} className="grid grid-cols-[2.5fr_1fr_0.8fr_0.8fr_0.5fr] gap-3 px-4 py-3 border-b border-border/30 hover:bg-muted/20 transition-colors items-center" data-testid={`kb-doc-${doc.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0", srcCfg.bg)}>
                        <SrcIcon className={cn("w-4 h-4", srcCfg.color)} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{doc.title}</p>
                        <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
                          {doc.source_type === 'url' && <><Link2 className="w-2.5 h-2.5" />{doc.source_ref}</>}
                          {doc.source_type === 'file' && <><File className="w-2.5 h-2.5" />{doc.source_ref}</>}
                        </p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="text-xs w-fit">{doc.category}</Badge>
                    <span className="text-xs text-muted-foreground font-mono">{doc.total_chunks}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(doc.created_at)}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`kb-doc-actions-${doc.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-card border-border">
                        <DropdownMenuItem onClick={() => setDetailDocId(doc.id)}><Eye className="w-3.5 h-3.5 mr-2" />View Chunks</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setEditingDoc(doc); setEditTitle(doc.title); setEditCategory(doc.category); }}><Edit className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                        {doc.source_type === 'url' && <DropdownMenuItem onClick={() => handleResync(doc.id)}><RefreshCw className="w-3.5 h-3.5 mr-2" />Re-sync</DropdownMenuItem>}
                        {doc.source_type === 'url' && <DropdownMenuItem onClick={() => window.open(doc.source_ref, '_blank')}><ExternalLink className="w-3.5 h-3.5 mr-2" />Open URL</DropdownMenuItem>}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-400" onClick={() => handleDelete(doc.id)}><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </Card>

      {/* Dialogs */}
      <URLIngestDialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen} onSuccess={loadData} />
      <FileUploadDialog open={fileDialogOpen} onOpenChange={setFileDialogOpen} onSuccess={loadData} />
      <DocumentDetailDialog open={!!detailDocId} onOpenChange={() => setDetailDocId(null)} docId={detailDocId} />

      {/* Edit Dialog */}
      <Dialog open={!!editingDoc} onOpenChange={() => setEditingDoc(null)}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="edit-doc-dialog">
          <DialogHeader><DialogTitle>Edit Document</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Title</Label>
              <Input data-testid="edit-doc-title" value={editTitle} onChange={e => setEditTitle(e.target.value)} />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={editCategory} onValueChange={setEditCategory}>
                <SelectTrigger data-testid="edit-doc-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditingDoc(null)}>Cancel</Button>
            <Button onClick={handleEditSave} data-testid="edit-doc-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
